/**
 * 前回ログインアカウントの記録（localStorage）と表示用整形。
 * Vue 非依存の純関数のみ。リアクティブな読み書きは
 * composables/useLastLoginAccount.ts 側で行う。
 */

export type LastLoginProvider =
  'google' | 'apple' | 'facebook' | 'line' | 'password'

export type LastLoginAccount = {
  provider: LastLoginProvider
  displayName: string
  email: string
  photoURL: string
  savedAt: number
}

const LAST_LOGIN_ACCOUNT_KEY = 'yagish_last_login_account'
const LAST_LOGIN_DISMISSED_AT_KEY = 'yagish_last_login_dismissed_at'

/** ×で閉じたあと再表示を抑止する期間 */
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000

export const PROVIDER_LABELS: Record<LastLoginProvider, string> = {
  google: 'Google',
  apple: 'Apple',
  facebook: 'Facebook',
  line: 'LINE',
  password: 'メールアドレス'
}

const VALID_PROVIDERS: LastLoginProvider[] = [
  'google',
  'apple',
  'facebook',
  'line',
  'password'
]

/**
 * IDトークンの firebase.sign_in_provider をアプリ内のプロバイダ名へ変換する。
 * - password: メール+パスワード / メールリンクの両方（トークン上は区別できない）
 * - custom: LINE ログインはカスタムトークン経由のため custom になる。
 *   web のカスタムトークン経路は実質 LINE のみなので line とみなす（近似）
 */
export function providerFromSignInProvider(
  signInProvider: string | null | undefined
): LastLoginProvider | null {
  switch (signInProvider) {
    case 'google.com':
      return 'google'
    case 'apple.com':
      return 'apple'
    case 'facebook.com':
      return 'facebook'
    case 'custom':
      return 'line'
    case 'password':
      return 'password'
    default:
      return null
  }
}

/** Safari プライベートモード等で localStorage が投げても壊れないように包む */
function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch (e) {
    /* 保存できない環境ではレコメンドが出ないだけ */
  }
}

export function readLastLoginAccount(): LastLoginAccount | null {
  const raw = safeGet(LAST_LOGIN_ACCOUNT_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    if (!VALID_PROVIDERS.includes(parsed.provider)) return null
    return {
      provider: parsed.provider,
      displayName:
        typeof parsed.displayName === 'string' ? parsed.displayName : '',
      email: typeof parsed.email === 'string' ? parsed.email : '',
      photoURL: typeof parsed.photoURL === 'string' ? parsed.photoURL : '',
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : 0
    }
  } catch (e) {
    return null
  }
}

export function writeLastLoginAccount(account: LastLoginAccount): void {
  safeSet(LAST_LOGIN_ACCOUNT_KEY, JSON.stringify(account))
}

export function readDismissedAt(): number {
  const raw = safeGet(LAST_LOGIN_DISMISSED_AT_KEY)
  const parsed = raw ? Number(raw) : 0
  return Number.isFinite(parsed) ? parsed : 0
}

export function writeDismissedAt(at: number): void {
  safeSet(LAST_LOGIN_DISMISSED_AT_KEY, String(at))
}

export function isDismissCoolingDown(
  dismissedAt: number,
  now: number
): boolean {
  return dismissedAt > 0 && now - dismissedAt < DISMISS_COOLDOWN_MS
}

/**
 * メールアドレスを表示用にマスクする（例: ya****@example.com）。
 * ローカル部2文字まで残す。形式が崩れていたら全体をマスク
 */
export function maskEmail(email: string): string {
  if (!email) return ''
  const atIndex = email.indexOf('@')
  if (atIndex <= 0) return '****'
  const local = email.slice(0, atIndex)
  const domain = email.slice(atIndex)
  const visible = local.slice(0, Math.min(2, local.length))
  return `${visible}****${domain}`
}
